/**
 * ScheduleCard Component
 * Displays a schedule in a card format with icon, name, type badge, step count, etc.
 */
import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Typography,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  AccessTime as TimeIcon,
  Send as SendIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CHOICE_BOARD_MAX_CHOICES, Schedule, ScheduleType, formatDuration } from '../../types/schedule';

/** Send now on a routine or board card (BA-85 S2, S5, S7) */
export interface SendNowProps {
  onSend: () => void;
  /** 'disabled': another send is in flight, the page is offline, or the live state has not loaded */
  state: 'idle' | 'sending' | 'disabled';
  /** The S7 message, already translated */
  error?: string;
  ariaLabel: string;
}

interface ScheduleCardProps {
  schedule: Schedule;
  /** Absent: no Send now (a day or week plan, a template, or a viewer) */
  sendNow?: SendNowProps;
  /** The live node says this schedule is on the tablet now */
  isOnTablet?: boolean;
  onClick?: () => void;
  onEdit?: () => void;
  onDuplicate?: () => void;
  onDelete?: () => void;
  onToggleActive?: () => void;
}

export const TYPE_COLORS: Record<ScheduleType, string> = {
  routine: '#4caf50',
  daily_schedule: '#2196f3',
  first_then: '#ff9800',
  choice_board: '#9c27b0',
  waiting_board: '#607d8b',
  weekly_schedule: '#00bcd4',
};

export const ScheduleCard: React.FC<ScheduleCardProps> = ({
  schedule,
  onClick,
  onEdit,
  onDuplicate,
  onDelete,
  onToggleActive,
  sendNow,
  isOnTablet = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleAction = (action: (() => void) | undefined) => {
    handleMenuClose();
    action?.();
  };

  // Calculate step count - for weekly schedules, sum steps across all days
  const stepCount = schedule.type === 'weekly_schedule' && schedule.days
    ? Object.values(schedule.days).reduce((total, dayConfig) => {
        if (dayConfig?.steps) {
          return total + Object.keys(dayConfig.steps).length;
        }
        return total;
      }, 0)
    : schedule.steps?.length ?? 0;
  // BA-92 L1: the tablet shows only the first 3 choices of this board
  const overCap = schedule.type === 'choice_board' && stepCount > CHOICE_BOARD_MAX_CHOICES;
  const icon = schedule.icon.type === 'emoji' ? schedule.icon.emoji : '📋';
  const typeColor = TYPE_COLORS[schedule.type] || '#757575';

  // Count enabled days for weekly schedule
  const enabledDaysCount = schedule.type === 'weekly_schedule' && schedule.days
    ? Object.values(schedule.days).filter(d => d?.isEnabled).length
    : 0;

  // Format recurrence summary for display
  const getRecurrenceSummary = (): string => {
    if (!schedule.recurrence || schedule.recurrence.type === 'manual') {
      return t('schedules.recurrence.manual');
    }

    const { type, startTime, daysOfWeek } = schedule.recurrence;
    let summary = '';

    switch (type) {
      case 'daily':
        summary = t('schedules.recurrence.daily');
        break;
      case 'weekdays':
        summary = t('schedules.recurrence.weekdays');
        break;
      case 'weekends':
        summary = t('schedules.recurrence.weekends');
        break;
      case 'weekly':
        if (daysOfWeek && daysOfWeek.length > 0) {
          // Show abbreviated days: "Mon, Wed, Fri"
          const dayLabels = daysOfWeek.map(day => t(`schedules.days.${day}`));
          summary = dayLabels.join(', ');
        } else {
          summary = t('schedules.recurrence.weekly');
        }
        break;
      case 'one_time':
        summary = t('schedules.recurrence.one_time');
        break;
      default:
        summary = t(`schedules.recurrence.${type}` as any);
    }

    // Append time if set
    if (startTime) {
      summary += ` @ ${startTime}`;
    }

    return summary;
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? {
          boxShadow: 6,
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out',
        } : {},
      }}
      onClick={onClick}
    >
      {/* An inactive card dims its content only: Send now keeps full contrast (m9) */}
      <CardContent sx={{ flexGrow: 1, pb: 1, opacity: schedule.isActive ? 1 : 0.7 }}>
        {/* Header with icon and menu */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
          {/* Icon */}
          <Box
            sx={{
              fontSize: '2rem',
              mr: 1.5,
              mt: 0.5,
            }}
          >
            {icon}
          </Box>

          {/* Title and badges */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="h6"
              component="h3"
              noWrap
              sx={{ fontWeight: 500, lineHeight: 1.2 }}
            >
              {schedule.name}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
              {/* Type badge */}
              <Chip
                label={t(`schedules.types.${schedule.type}`)}
                size="small"
                sx={{
                  bgcolor: typeColor,
                  color: 'white',
                  height: 20,
                  '& .MuiChip-label': { px: 1, fontSize: '0.7rem' },
                }}
              />
              {/* Active status */}
              {!schedule.isActive && (
                <Chip
                  label={t('schedules.filters.inactive')}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
                />
              )}
              {isOnTablet && (
                <Chip
                  label={`● ${t('schedules.onTabletChip')}`}
                  size="small"
                  variant="outlined"
                  color="primary"
                  sx={{ height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
                />
              )}
            </Box>
          </Box>

          {/* Menu button: a 48 px touch target at every width (BA-97); the negative
              margins pull it into the card's padding so the header does not grow */}
          <IconButton
            aria-label={t('schedules.card.menuAria')}
            size="small"
            onClick={handleMenuOpen}
            sx={{ ml: 0.5, mt: -1, mr: -1, width: 48, height: 48, flexShrink: 0 }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Description */}
        {schedule.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {schedule.description}
          </Typography>
        )}

        {/* Stats row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            color: 'text.secondary',
            fontSize: '0.875rem',
            mt: 'auto',
          }}
        >
          {/* For weekly schedules, show enabled days count */}
          {schedule.type === 'weekly_schedule' ? (
            <Typography variant="body2" color="text.secondary">
              {enabledDaysCount}/7 {t('schedules.recurrence.daysOfWeek').toLowerCase()}
            </Typography>
          ) : (
            /* Step count for other schedule types */
            <Typography variant="body2" color="text.secondary">
              {t('schedules.stepsCount', { count: stepCount })}
            </Typography>
          )}

          {/* Total steps for weekly (across all days) */}
          {schedule.type === 'weekly_schedule' && stepCount > 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('schedules.stepsCount', { count: stepCount })}
            </Typography>
          )}

          {/* Duration */}
          {schedule.estimatedDurationSeconds && schedule.estimatedDurationSeconds > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <TimeIcon fontSize="small" sx={{ fontSize: 16 }} />
              <Typography variant="body2" color="text.secondary">
                {t('schedules.estimatedDuration', {
                  duration: formatDuration(schedule.estimatedDurationSeconds),
                })}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Recurrence */}
        {schedule.recurrence && schedule.recurrence.type !== 'manual' && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.5 }}
          >
            {getRecurrenceSummary()}
          </Typography>
        )}
      </CardContent>

      {(sendNow || overCap) && (
        <CardActions
          sx={{ px: 2, pb: 2, pt: 0, flexDirection: 'column', alignItems: isPhone ? 'stretch' : 'flex-start' }}
          disableSpacing
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: isPhone ? 'column' : 'row',
              flexWrap: 'wrap',
              alignItems: isPhone ? 'stretch' : 'center',
              gap: 1,
            }}
          >
            {sendNow && (
              <Button
                variant="outlined"
                startIcon={sendNow.state === 'sending' ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                disabled={sendNow.state !== 'idle' || stepCount === 0}
                aria-label={sendNow.ariaLabel}
                fullWidth={isPhone}
                onClick={(e) => {
                  // The card's own click opens the editor
                  e.stopPropagation();
                  sendNow.onSend();
                }}
                sx={isPhone ? { minHeight: 48 } : undefined}
              >
                {sendNow.state === 'sending' ? t('schedules.sending') : t('schedules.sendNow')}
              </Button>
            )}
            {overCap && (
              <Chip
                icon={<WarningIcon />}
                label={t('schedules.card.overCap', { count: stepCount })}
                aria-label={t('schedules.card.overCapAria', { count: stepCount })}
                size="small"
                variant="outlined"
                color="warning"
                sx={{
                  alignSelf: isPhone ? 'flex-start' : 'center',
                  height: 28,
                  bgcolor: '#fff4e5',
                  color: 'warning.dark',
                  '& .MuiChip-label': { fontSize: 13 },
                }}
              />
            )}
          </Box>
          {sendNow && stepCount === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('schedules.addStepToSend')}
            </Typography>
          )}
          {sendNow?.error && (
            <Alert severity="error" sx={{ mt: 1.5, alignSelf: 'stretch' }} onClick={(e) => e.stopPropagation()}>
              {sendNow.error}
            </Alert>
          )}
        </CardActions>
      )}

      {/* Context menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        {onEdit && (
          <MenuItem onClick={() => handleAction(onEdit)}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('schedules.actions.edit')}</ListItemText>
          </MenuItem>
        )}
        {onDuplicate && (
          <MenuItem onClick={() => handleAction(onDuplicate)}>
            <ListItemIcon>
              <DuplicateIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('schedules.actions.duplicate')}</ListItemText>
          </MenuItem>
        )}
        {onToggleActive && (
          <MenuItem onClick={() => handleAction(onToggleActive)}>
            <ListItemIcon>
              {schedule.isActive ? (
                <InactiveIcon fontSize="small" />
              ) : (
                <ActiveIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>
              {schedule.isActive
                ? t('schedules.actions.deactivate')
                : t('schedules.actions.activate')}
            </ListItemText>
          </MenuItem>
        )}
        {onDelete && (
          <MenuItem onClick={() => handleAction(onDelete)} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>{t('schedules.actions.delete')}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Card>
  );
};
